import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { BeatLoader } from 'react-spinners'
import Sidebar from '../../layouts/Sidebar'
import Topbar from '../../layouts/Topbar'
import api from '../../config/api'

/* ─── helpers ────────────────────────────────────────────────────────────── */

const getErrorMessage = (err, fallback) => {
    const data = err.response?.data
    if (!data) return fallback
    if (typeof data === 'string') return data
    if (data.detail) return data.detail
    if (data.message) return data.message
    return Object.entries(data)
        .map(([f, m]) => `${f}: ${Array.isArray(m) ? m.join(', ') : m}`)
        .join(' | ')
}

const getClientName = (q) => {
    const d = q.client_details
    if (!d) return q.client_name || '—'
    if (d.display_name) return d.display_name
    const name = [d.first_name, d.last_name].filter(Boolean).join(' ')
    return name || q.client_name || '—'
}

const formatDate = (dateStr) => {
    if (!dateStr) return '—'
    const d = new Date(dateStr)
    return Number.isNaN(d.getTime())
        ? dateStr
        : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

const formatAmount = (amount, currency) => {
    if (amount == null || amount === '') return '—'
    const num = Number(amount)
    const formatted = Number.isNaN(num)
        ? amount
        : num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    if (currency === 'USD') return `$${formatted}`
    if (currency === 'ZIG') return `ZIG ${formatted}`
    return formatted
}

const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase?.()) {
        case 'accepted':  return 'badge-soft-success'
        case 'draft':     return 'badge-soft-warning'
        case 'sent':      return 'badge-soft-primary'
        case 'declined':
        case 'expired':   return 'badge-soft-danger'
        default:          return 'badge-soft-secondary'
    }
}

const formatStatusLabel = (s) =>
    s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '—'

const addDays = (n) => {
    const d = new Date()
    d.setDate(d.getDate() + n)
    return d.toISOString().split('T')[0]
}

/* ─── component ──────────────────────────────────────────────────────────── */

const Quotations = () => {
    const [quotations, setQuotations]       = useState([])
    const [loading, setLoading]             = useState(true)
    const [error, setError]                 = useState(null)

    // filters
    const [search, setSearch]               = useState('')
    const [statusFilter, setStatusFilter]   = useState('')

    // action feedback
    const [actionMsg, setActionMsg]         = useState(null)
    const [actionErr, setActionErr]         = useState(null)

    // Convert-to-Invoice modal
    const [converting, setConverting]       = useState(false)
    const [convertTarget, setConvertTarget] = useState(null)
    const [dueDate, setDueDate]             = useState(addDays(30))
    const [convertBusy, setConvertBusy]     = useState(false)

    /* ── fetch ── */
    const fetchQuotations = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const params = {}
            if (statusFilter) params.status = statusFilter
            if (search)       params.search = search
            const res = await api.get('/quotations/', { params })
            setQuotations(Array.isArray(res.data) ? res.data : (res.data?.results ?? []))
        } catch (err) {
            console.error('Failed to fetch quotations', err)
            setError(getErrorMessage(err, 'Failed to load quotations. Please try again.'))
        } finally {
            setLoading(false)
        }
    }, [statusFilter, search])

    useEffect(() => {
        fetchQuotations()
    }, [fetchQuotations])

    /* ── delete ── */
    const handleDelete = async (q) => {
        const label = q.quotation_number || 'this quotation'
        if (!window.confirm(`Permanently delete ${label}? This cannot be undone.`)) return
        setActionMsg(null)
        setActionErr(null)
        try {
            await api.delete(`/quotations/${q.id}/`)
            setActionMsg(`${label} deleted.`)
            fetchQuotations()
        } catch (err) {
            setActionErr(getErrorMessage(err, 'Failed to delete quotation.'))
        }
    }

    /* ── duplicate ── */
    const handleDuplicate = async (q) => {
        setActionMsg(null)
        setActionErr(null)
        try {
            await api.post(`/quotations/${q.id}/duplicate/`)
            setActionMsg(`Duplicated ${q.quotation_number} as a new draft.`)
            fetchQuotations()
        } catch (err) {
            setActionErr(getErrorMessage(err, 'Failed to duplicate quotation.'))
        }
    }

    /* ── convert to invoice ── */
    const openConvertModal = (q) => {
        setConvertTarget(q)
        setDueDate(addDays(30))
        setConverting(true)
    }

    const handleConvert = async () => {
        if (!convertTarget || !dueDate) return
        setConvertBusy(true)
        setActionMsg(null)
        setActionErr(null)
        try {
            await api.post(`/quotations/${convertTarget.id}/convert-to-invoice/`, { due_date: dueDate })
            setActionMsg(`${convertTarget.quotation_number} converted to a draft invoice.`)
            setConverting(false)
            fetchQuotations()
        } catch (err) {
            setActionErr(getErrorMessage(err, 'Failed to convert quotation.'))
            setConverting(false)
        } finally {
            setConvertBusy(false)
        }
    }

    /* ── render ── */
    return (
        <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
            <Topbar />
            <Sidebar />

            <div className="page-wrapper">
                <div className="content content-two pb-0">

                    {/* Page header */}
                    <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-3 mb-3">
                        <h5 className="d-flex align-items-center mb-0">
                            Quotations
                            {!loading && !error && (
                                <span className="badge bg-soft-dark ms-2 text-dark fs-12">
                                    {quotations.length} {quotations.length === 1 ? 'Quotation' : 'Quotations'}
                                </span>
                            )}
                        </h5>
                        <Link to="/create-quotation" className="btn btn-md btn-primary d-flex align-items-center">
                            <i className="ti ti-circle-plus me-2" />
                            Add Quotation
                        </Link>
                    </div>

                    {/* Action feedback */}
                    {actionMsg && (
                        <div className="alert alert-success alert-dismissible mb-3" role="alert">
                            <i className="ti ti-check me-2" />{actionMsg}
                            <button type="button" className="btn-close" onClick={() => setActionMsg(null)} />
                        </div>
                    )}
                    {actionErr && (
                        <div className="alert alert-danger alert-dismissible mb-3" role="alert">
                            <i className="ti ti-alert-circle me-2" />{actionErr}
                            <button type="button" className="btn-close" onClick={() => setActionErr(null)} />
                        </div>
                    )}

                    <div className="card shadow-sm">
                        <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
                            <h5 className="fw-bold mb-0">All Quotations</h5>

                            {/* Filters */}
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    style={{ minWidth: 180 }}
                                    placeholder="Search quotations…"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                                <select
                                    className="form-select form-select-sm"
                                    style={{ minWidth: 130 }}
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <option value="">All Statuses</option>
                                    <option value="draft">Draft</option>
                                    <option value="sent">Sent</option>
                                    <option value="accepted">Accepted</option>
                                    <option value="declined">Declined</option>
                                    <option value="expired">Expired</option>
                                </select>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-white"
                                    onClick={fetchQuotations}
                                    disabled={loading}
                                >
                                    <i className="ti ti-refresh me-1" />
                                    Refresh
                                </button>
                            </div>
                        </div>

                        <div className="card-body p-0">
                            {loading && (
                                <div className="d-flex justify-content-center align-items-center py-5">
                                    <BeatLoader color="#3550DC" size={10} />
                                </div>
                            )}

                            {!loading && error && (
                                <div className="alert alert-danger m-3 mb-0" role="alert">
                                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                                        <span><i className="ti ti-alert-circle me-2" />{error}</span>
                                        <button type="button" className="btn btn-sm btn-danger" onClick={fetchQuotations}>
                                            Retry
                                        </button>
                                    </div>
                                </div>
                            )}

                            {!loading && !error && quotations.length === 0 && (
                                <div className="text-center py-5">
                                    <div className="avatar avatar-lg bg-light text-muted mx-auto mb-3">
                                        <i className="ti ti-file-description fs-24" />
                                    </div>
                                    <h6 className="fw-semibold mb-1">No quotations found</h6>
                                    <p className="text-muted mb-3">Create your first quotation to send to a client.</p>
                                    <Link to="/create-quotation" className="btn btn-primary">
                                        <i className="ti ti-plus me-1" />
                                        Add Quotation
                                    </Link>
                                </div>
                            )}

                            {!loading && !error && quotations.length > 0 && (
                                <div className="table-responsive">
                                    <table className="table table-nowrap border mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Quotation #</th>
                                                <th>Client</th>
                                                <th>Issue Date</th>
                                                <th>Currency</th>
                                                <th>Grand Total</th>
                                                <th>Status</th>
                                                <th className="text-end">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {quotations.map((q) => (
                                                <tr key={q.id}>
                                                    <td>
                                                        <span className="fw-medium text-dark">
                                                            {q.quotation_number || '—'}
                                                        </span>
                                                    </td>
                                                    <td>{getClientName(q)}</td>
                                                    <td>{formatDate(q.issue_date)}</td>
                                                    <td>{q.currency || '—'}</td>
                                                    <td>{formatAmount(q.grand_total, q.currency)}</td>
                                                    <td>
                                                        <span className={`badge ${getStatusBadgeClass(q.status)}`}>
                                                            {formatStatusLabel(q.status)}
                                                        </span>
                                                    </td>
                                                    <td className="text-end">
                                                        <div className="d-inline-flex align-items-center gap-1 flex-wrap">
                                                            <Link
                                                                to={`/quotation-details/${q.id}`}
                                                                className="btn btn-sm btn-outline-primary"
                                                            >
                                                                <i className="ti ti-eye me-1" />
                                                                Details
                                                            </Link>
                                                            
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-info"
                                                                onClick={() => handleDuplicate(q)}
                                                                title="Duplicate"
                                                            >
                                                                <i className="ti ti-copy me-1" />
                                                                Duplicate
                                                            </button>
                                                            {q.status !== 'accepted' && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-success"
                                                                    onClick={() => openConvertModal(q)}
                                                                    title="Convert to Invoice"
                                                                >
                                                                    <i className="ti ti-file-invoice me-1" />
                                                                    → Invoice
                                                                </button>
                                                            )}
                                                            
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Convert to Invoice Modal ── */}
            {converting && convertTarget && (
                <div
                    className="modal fade show d-block"
                    tabIndex="-1"
                    role="dialog"
                    style={{ background: 'rgba(0,0,0,0.5)' }}
                    onClick={(e) => { if (e.target === e.currentTarget) setConverting(false) }}
                >
                    <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <i className="ti ti-file-invoice me-2" />
                                    Convert to Invoice
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setConverting(false)}
                                />
                            </div>
                            <div className="modal-body">
                                <p className="text-muted mb-3">
                                    Converting <strong>{convertTarget.quotation_number}</strong> will create
                                    a draft invoice and mark this quotation as <strong>Accepted</strong>.
                                </p>
                                <label htmlFor="convert-due-date" className="form-label">
                                    Invoice Due Date <span className="text-danger">*</span>
                                </label>
                                <input
                                    id="convert-due-date"
                                    type="date"
                                    className="form-control"
                                    value={dueDate}
                                    onChange={(e) => setDueDate(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-light"
                                    onClick={() => setConverting(false)}
                                    disabled={convertBusy}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-success"
                                    onClick={handleConvert}
                                    disabled={convertBusy || !dueDate}
                                >
                                    {convertBusy
                                        ? <BeatLoader color="#fff" size={8} />
                                        : <><i className="ti ti-check me-1" />Convert</>
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default Quotations
